import { useState, useRef, useEffect, useCallback } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { useDemo, useAppMode } from '../context/DemoContext';
import { explorerTx } from '../lib/solana/devnet';
import { useChancellorHeartbeat } from '../hooks/useChancellorHeartbeat';
import { Send, Loader2, Sparkles, Coins, Activity, Scroll, Plus, MessageSquare, ChevronRight, X, Crown, ArrowLeft, Eye } from 'lucide-react';
import { Link } from 'react-router-dom';
import chancellorPortraitHead from '../assets/chancellor/specter.jpg';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const BASE58_RE = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;

// Lore-themed Chancellor palette
//   Obsidian void:    #050208
//   Deep crimson:     #2a0610
//   Ember rust:       #8a2818
//   Gilded amber:     #c89048  (primary accent)
//   Ash white:        #e6dcc8  (text)

function genSessionId() {
  let s = '';
  try { s = localStorage.getItem('gfg_chancellor_session') || ''; } catch (_) {}
  if (!s) {
    s = 'chan-' + Math.random().toString(36).slice(2, 10) + '-' + Date.now().toString(36);
    try { localStorage.setItem('gfg_chancellor_session', s); } catch (_) {}
  }
  return s;
}

const SUGGESTED_OPENINGS = [
  { icon: Sparkles, text: 'Read my soul. What does the chain say of me?' },
  { icon: Coins, text: 'I hold a meme-token. Should I be patient or panic?' },
  { icon: Scroll, text: 'What is the Genesis Passport, Chancellor?' },
  { icon: Activity, text: 'Tell me of the Forge. Why does the hammer fall?' },
];

const TIER_COLORS = {
  DEGEN:    '#ff6464',
  ACTIVE:   '#ffaa50',
  CASUAL:   '#dcc878',
  SLEEPING: '#a0a0a0',
  DORMANT:  '#6e6e82',
};

export default function ChancellorPage() {
  const { publicKey } = useWallet();
  const { isDemo, demoWallet } = useDemo();
  const { isLive } = useAppMode();
  const wallet = publicKey?.toString() || (isDemo && demoWallet) || '';

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(genSessionId);
  const [scanMode, setScanMode] = useState('wallet'); // 'wallet' | 'token'
  // Live heartbeat — drives the "awake Xh Ym" presiding pill
  const heartbeat = useChancellorHeartbeat();
  const [scanWallet, setScanWallet] = useState('');
  const [scanError, setScanError] = useState('');
  const [scanOpen, setScanOpen] = useState(false);
  const [shareCard, setShareCard] = useState(null);
  const [recentReadings, setRecentReadings] = useState([]);
  const [whispers, setWhispers] = useState(null); // {whispers:[], watched_count, first_visit}
  const [whisperOpen, setWhisperOpen] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false); // mobile drawer
  const [desktopSidebarOpen, setDesktopSidebarOpen] = useState(() => {
    // Default: closed. Only show if user has explicitly opened it before.
    try { return localStorage.getItem('chan_sidebar_closed') === '0'; } catch { return false; }
  });
  const toggleDesktopSidebar = () => {
    setDesktopSidebarOpen((v) => {
      const next = !v;
      try { localStorage.setItem('chan_sidebar_closed', next ? '0' : '1'); } catch { /* ignore */ }
      return next;
    });
  };
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll on new message + keep scrolling while a chancellor reply is typing out
  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    // If the latest message is a fresh chancellor reply, keep auto-scrolling for ~10s
    // so the bubble's growing height (typewriter) stays pinned to bottom.
    const last = messages[messages.length - 1];
    if (!last || last.role !== 'chancellor' || messages.length < 2) return;
    let cancelled = false;
    const startedAt = Date.now();
    const tick = () => {
      if (cancelled || !scrollRef.current) return;
      // Only auto-scroll if the user is already near the bottom (don't yank them up)
      const el = scrollRef.current;
      const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      if (distFromBottom < 220) {
        el.scrollTop = el.scrollHeight;
      }
      if (Date.now() - startedAt < 10000) requestAnimationFrame(tick);
    };
    const raf = requestAnimationFrame(tick);
    return () => { cancelled = true; cancelAnimationFrame(raf); };
  }, [messages, loading]);

  // Live ticker stats — fetch on mount + every 60s
  // Recent readings — load once + refresh after each scan
  const loadRecent = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/chancellor/recent-readings?limit=6`);
      if (!r.ok) return;
      const d = await r.json();
      setRecentReadings(d.readings || []);
    } catch (_) {}
  }, []);
  useEffect(() => { loadRecent(); }, [loadRecent]);

  // WHISPER NETWORK — on mount, ask the Chancellor what stirred while we slept
  useEffect(() => {
    let cancelled = false;
    const loadWhispers = async () => {
      try {
        const r = await fetch(`${API_URL}/api/chancellor/whispers?session_id=${encodeURIComponent(sessionId)}`);
        if (!r.ok) return;
        const d = await r.json();
        if (!cancelled) setWhispers(d);
      } catch (_) {}
    };
    loadWhispers();
  }, [sessionId]);

  const send = useCallback(async (text, opts = {}) => {
    const trimmed = (text || input).trim();
    if (!trimmed || loading) return;
    const targetWallet = (opts.scanWallet ?? '').trim();
    const researchToken = (opts.researchToken ?? '').trim();
    setInput('');
    setMessages((m) => [...m, {
      role: 'soul', text: trimmed,
      scanTarget: targetWallet || null,
      researchTarget: researchToken || null,
    }]);
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/chancellor/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: trimmed,
          session_id: sessionId,
          wallet_address: wallet || null,
          scan_wallet: targetWallet || null,
          research_token: researchToken || null,
          live_mode: isLive && !!targetWallet,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'The Chancellor falls silent.');
      setMessages((m) => [...m, {
        role: 'chancellor',
        text: data.reply,
        walletSeen: data.wallet_seen,
        targetSeen: data.target_seen,
        tokenSeen: data.token_seen,
        scanTarget: data.scan_wallet,
        researchedToken: data.researched_token,
        soulCardId: data.soul_card_id,
        memoryRecalled: data.memory_recalled,
        // On-chain proof — populated when backend wrote record_chancellor.
        judgment: data.judgment || null,
        txSignature: data.judgment?.tx_signature,
        txIsMock: data.judgment?.tx_signature_is_mock !== false,
        onchainError: data.judgment?.onchain_error || null,
      }]);
      if (targetWallet) loadRecent();
    } catch (e) {
      setMessages((m) => [...m, { role: 'chancellor', text: `(${e.message || 'silence'})`, error: true }]);
    } finally {
      setLoading(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [input, loading, sessionId, wallet, loadRecent, isLive]);

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const submitScan = useCallback(() => {
    const addr = scanWallet.trim();
    if (!addr) {
      setScanError(scanMode === 'token' ? 'paste a ticker or mint' : 'paste a solana address');
      return;
    }
    if (scanMode === 'wallet') {
      if (!BASE58_RE.test(addr)) {
        setScanError('not a valid solana address');
        return;
      }
      setScanError('');
      send('Render judgement upon this soul. Read what the chain reveals, and do not be gentle.', { scanWallet: addr });
    } else {
      // Token mode — accept ticker (with or without $) or mint address
      const cleaned = addr.replace(/^\$/, '').trim();
      if (cleaned.length < 2 || cleaned.length > 64) {
        setScanError('ticker or mint required');
        return;
      }
      setScanError('');
      send(`Investigate this token. Render verdict.`, { researchToken: cleaned });
    }
  }, [scanWallet, scanMode, send]);

  const pasteScan = async () => {
    try {
      const txt = await navigator.clipboard.readText();
      if (txt) {
        setScanWallet(txt.trim());
        setScanError('');
      }
    } catch (_) { /* ignore */ }
  };

  const newAudience = () => {
    setMessages([]);
    setScanWallet('');
    setScanError('');
    setScanOpen(false);
    setSidebarOpen(false);
  };

  return (
    <div className="relative min-h-screen w-full overflow-x-clip flex" data-testid="chancellor-page"
      style={{ background: '#050208', color: '#e6dcc8' }}>
      <style>{`
        @keyframes chan-mask-breathe {
          0%, 100% { filter: brightness(0.85) drop-shadow(0 0 20px rgba(140,40,20,0.5)) drop-shadow(0 0 60px rgba(120,30,15,0.25)); transform: translateY(0); }
          50%      { filter: brightness(1.05) drop-shadow(0 0 36px rgba(200,144,72,0.55)) drop-shadow(0 0 90px rgba(180,80,30,0.3)); transform: translateY(-3px); }
        }
        @keyframes chan-eye-glow {
          0%, 100% { opacity: 0.85; box-shadow: 0 0 14px #c89048, 0 0 28px rgba(200,144,72,0.55); }
          50%      { opacity: 1; box-shadow: 0 0 22px #ffaa30, 0 0 50px rgba(255,140,40,0.7); }
        }
        @keyframes chan-rune-fall {
          0%   { transform: translateY(-10vh); opacity: 0; }
          15%  { opacity: 0.18; }
          88%  { opacity: 0.12; }
          100% { transform: translateY(110vh); opacity: 0; }
        }
        @keyframes chan-typing {
          0% { opacity: 0.3; }
          50% { opacity: 1; }
          100% { opacity: 0.3; }
        }
        @keyframes chan-msg-in {
          0%   { opacity: 0; transform: translateY(8px); filter: blur(2px); }
          100% { opacity: 1; transform: translateY(0); filter: blur(0); }
        }
        @keyframes chan-fade-in {
          0% { opacity: 0; } 100% { opacity: 1; }
        }
        @keyframes chan-pulse {
          0%, 100% { opacity: 0.75; }
          50% { opacity: 1; }
        }
        @keyframes chan-presiding-pulse {
          0%, 100% { opacity: 0.55; }
          50% { opacity: 1; }
        }
        .chan-scroll::-webkit-scrollbar { width: 6px; }
        .chan-scroll::-webkit-scrollbar-track { background: transparent; }
        .chan-scroll::-webkit-scrollbar-thumb { background: rgba(200,144,72,0.18); border-radius: 0; }
        .chan-scroll::-webkit-scrollbar-thumb:hover { background: rgba(200,144,72,0.35); }
      `}</style>

      {/* === ATMOSPHERE === */}
      <div className="fixed inset-0 z-0 pointer-events-none" style={{
        background: 'radial-gradient(ellipse at 35% 20%, rgba(60,12,20,0.55) 0%, rgba(20,4,12,0.85) 35%, rgba(8,0,4,0.95) 70%, #050208 100%)',
      }} />
      {Array.from({ length: 6 }).map((_, i) => {
        const glyphs = ['✦','☩','⚜','☉','⚝','†'];
        const g = glyphs[i % glyphs.length];
        const left = (i * 17.3 + 12) % 100;
        const dur = 26 + (i * 2.1) % 12;
        const delay = -(i * 1.7) % 22;
        return (
          <span key={`run-${i}`} className="fixed font-serif select-none pointer-events-none z-0"
            style={{
              left: `${left}%`, top: '-8vh',
              fontSize: `${12 + (i % 3) * 5}px`,
              color: 'rgba(140,40,20,0.15)',
              animation: `chan-rune-fall ${dur}s linear ${delay}s infinite`,
            }}>{g}</span>
        );
      })}

      {/* === LEFT SIDEBAR === */}
      <Sidebar
        open={sidebarOpen}
        desktopOpen={desktopSidebarOpen}
        onClose={() => setSidebarOpen(false)}
        wallet={wallet}
        recentReadings={recentReadings}
        onNewAudience={newAudience}
        onPickReading={(r) => setShareCard({ id: r.card_id, scanTarget: r.wallet_short })}
      />

      {/* === MAIN COLUMN === */}
      <div className={`relative z-10 flex-1 flex flex-col min-w-0 min-h-screen transition-all duration-300 ${desktopSidebarOpen ? 'lg:ml-[280px]' : 'lg:ml-0'}`}>

        {/* === TOP STATUS BAR === */}
        <header className="sticky top-0 z-20 backdrop-blur-md flex items-center gap-2 sm:gap-4 pl-4 sm:pl-6 pr-[68px] sm:pr-6 py-3"
          style={{
            background: 'linear-gradient(180deg, rgba(10,4,8,0.85) 0%, rgba(8,2,6,0.7) 100%)',
            borderBottom: '1px solid rgba(200,144,72,0.18)',
          }}>
          {/* Back to The Halls — vibrant, always visible */}
          <Link to="/halls" data-testid="chancellor-back-halls"
            className="inline-flex items-center gap-1.5 sm:gap-2 font-mono uppercase tracking-[0.22em] sm:tracking-[0.28em] transition-all hover:scale-[1.04] shrink-0"
            style={{
              padding: '7px 11px',
              fontSize: '0.62rem',
              color: '#ffd680',
              background: 'linear-gradient(180deg, rgba(40,12,8,0.6) 0%, rgba(20,6,4,0.85) 100%)',
              border: '1px solid rgba(200,144,72,0.55)',
              borderRadius: 2,
              boxShadow: '0 0 14px rgba(200,144,72,0.28), inset 0 0 14px rgba(0,0,0,0.5)',
              textDecoration: 'none',
              textShadow: '0 0 8px rgba(200,144,72,0.5)',
            }}>
            <ArrowLeft className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
            <span className="hidden sm:inline">Halls</span>
          </Link>

          {/* Sidebar toggle — mobile opens drawer, desktop toggles collapse */}
          <button
            onClick={() => {
              if (window.matchMedia('(min-width: 1024px)').matches) {
                toggleDesktopSidebar();
              } else {
                setSidebarOpen(true);
              }
            }}
            data-testid="chan-sidebar-toggle"
            className="p-1.5 transition-all hover:scale-110 shrink-0"
            style={{ color: 'rgba(200,144,72,0.85)' }}
            title={desktopSidebarOpen ? 'Hide sidebar' : 'Show sidebar'}
          >
            {/* Show inward chevron when sidebar visible (collapse it), outward when hidden (open it).
                On mobile this stays as the standard menu icon via responsive class swap below. */}
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="hidden lg:block">
              {desktopSidebarOpen ? (
                <>
                  <rect x="3" y="3" width="18" height="18" rx="1" />
                  <line x1="9" y1="3" x2="9" y2="21" />
                </>
              ) : (
                <>
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <line x1="3" y1="12" x2="21" y2="12" />
                  <line x1="3" y1="18" x2="21" y2="18" />
                </>
              )}
            </svg>
            <MessageSquare className="w-4 h-4 lg:hidden" />
          </button>

          <div className="flex-1" />

          {/* Scan toggle — opens the Soul Scanner panel (always visible, mobile + desktop) */}
          <button
            onClick={() => setScanOpen(!scanOpen)}
            data-testid="chan-scan-toggle"
            className="inline-flex items-center gap-1.5 sm:gap-2 font-mono uppercase tracking-[0.22em] sm:tracking-[0.28em] transition-all hover:scale-[1.04] shrink-0"
            style={{
              padding: '7px 11px',
              fontSize: '0.62rem',
              color: scanOpen ? '#050208' : '#ffd680',
              background: scanOpen
                ? 'linear-gradient(180deg, #ffd680 0%, #c89048 100%)'
                : 'linear-gradient(180deg, rgba(40,12,8,0.6) 0%, rgba(20,6,4,0.85) 100%)',
              border: '1px solid rgba(200,144,72,0.55)',
              borderRadius: 2,
              boxShadow: scanOpen
                ? '0 0 16px rgba(255,214,128,0.5)'
                : '0 0 14px rgba(200,144,72,0.28), inset 0 0 14px rgba(0,0,0,0.5)',
              textShadow: scanOpen ? 'none' : '0 0 8px rgba(200,144,72,0.5)',
              fontWeight: 600,
            }}
            title={scanOpen ? 'Close scanner' : 'Open the Soul Scanner'}
          >
            <Eye className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
            <span className="hidden sm:inline">{scanOpen ? 'Close' : 'Scan'}</span>
          </button>

          {/* Status pill — minimal "presiding" indicator (md+ only to avoid global hamburger collision) */}
          <div className="hidden md:flex items-center gap-2 shrink-0"
            style={{
              padding: '6px 12px',
              background: 'linear-gradient(180deg, rgba(40,12,8,0.45), rgba(20,6,4,0.65))',
              border: '1px solid rgba(200,144,72,0.28)',
              borderRadius: 2,
            }}>
            <span style={{
              width: 6, height: 6, borderRadius: '50%',
              background: '#5be584', boxShadow: '0 0 8px #5be584',
              animation: 'chan-presiding-pulse 2s ease-in-out infinite',
            }} />
            <span className="font-mono uppercase"
              style={{
                fontSize: '0.6rem',
                letterSpacing: '0.32em',
                color: 'rgba(220,200,120,0.7)',
              }}>
              presiding
              {heartbeat.ready && heartbeat.awakeSeconds >= 60 && (
                <span className="hidden lg:inline" style={{
                  color: 'rgba(220,200,120,0.4)',
                  letterSpacing: '0.28em',
                  marginLeft: 8,
                }}>
                  · awake {heartbeat.awakeLabel}
                </span>
              )}
            </span>
          </div>
        </header>

        {/* === SCANNER PANEL (collapsible) === */}
        {scanOpen && (
          <div className="px-4 sm:px-6 lg:px-10 pt-5" data-testid="soul-scanner-panel">
            <div className="relative max-w-3xl mx-auto" style={{ animation: 'chan-fade-in 0.5s ease-out' }}>
              <div className="px-5 py-4"
                style={{
                  background: 'linear-gradient(180deg, rgba(30,8,6,0.7) 0%, rgba(15,4,3,0.8) 100%)',
                  border: '1px solid rgba(200,144,72,0.32)',
                  borderRadius: '3px',
                  boxShadow: '0 0 28px rgba(140,40,20,0.18)',
                }}>
                <div className="flex items-center justify-between mb-2.5">
                  <div className="flex items-center gap-1">
                    <button onClick={() => { setScanMode('wallet'); setScanError(''); }}
                      data-testid="scan-mode-wallet"
                      style={{
                        padding: '4px 10px',
                        fontSize: '9px',
                        fontFamily: 'monospace',
                        letterSpacing: '0.32em',
                        textTransform: 'uppercase',
                        color: scanMode === 'wallet' ? '#ffd680' : 'rgba(200,144,72,0.45)',
                        background: scanMode === 'wallet' ? 'rgba(200,144,72,0.12)' : 'transparent',
                        border: `1px solid ${scanMode === 'wallet' ? 'rgba(200,144,72,0.5)' : 'rgba(200,144,72,0.18)'}`,
                        borderRadius: '2px',
                        cursor: 'pointer',
                        fontWeight: scanMode === 'wallet' ? 700 : 500,
                      }}>
                      ⚖ wallet
                    </button>
                    <button onClick={() => { setScanMode('token'); setScanError(''); }}
                      data-testid="scan-mode-token"
                      style={{
                        padding: '4px 10px',
                        fontSize: '9px',
                        fontFamily: 'monospace',
                        letterSpacing: '0.32em',
                        textTransform: 'uppercase',
                        color: scanMode === 'token' ? '#ffd680' : 'rgba(200,144,72,0.45)',
                        background: scanMode === 'token' ? 'rgba(200,144,72,0.12)' : 'transparent',
                        border: `1px solid ${scanMode === 'token' ? 'rgba(200,144,72,0.5)' : 'rgba(200,144,72,0.18)'}`,
                        borderRadius: '2px',
                        cursor: 'pointer',
                        fontWeight: scanMode === 'token' ? 700 : 500,
                      }}>
                      🜍 token
                    </button>
                    <span className="ml-3 text-[8px] font-mono tracking-[0.32em] uppercase"
                      style={{ color: 'rgba(200,144,72,0.5)' }}>
                      {scanMode === 'wallet' ? '· paste any solana wallet' : '· paste ticker or mint'}
                    </span>
                  </div>
                  <button onClick={() => { setScanOpen(false); setScanWallet(''); setScanError(''); }}
                    data-testid="soul-scanner-close"
                    className="text-[10px] font-mono tracking-[0.2em] transition-opacity hover:opacity-100 flex items-center gap-1"
                    style={{ color: 'rgba(200,144,72,0.5)', opacity: 0.7 }}>
                    <X className="w-3 h-3" /> close
                  </button>
                </div>
                <div className="flex flex-col sm:flex-row items-stretch gap-2">
                  <input type="text" value={scanWallet}
                    onChange={(e) => { setScanWallet(e.target.value); setScanError(''); }}
                    placeholder={scanMode === 'wallet'
                      ? 'e.g. C2kQki...Y6S4 — any wallet, any soul'
                      : 'e.g. POPCAT, $WIF, or the mint address'}
                    data-testid="soul-scanner-input"
                    className="flex-1 bg-transparent outline-none font-mono text-xs placeholder:opacity-30 px-3 py-2"
                    style={{
                      color: '#e6dcc8',
                      letterSpacing: '0.04em',
                      border: '1px solid rgba(200,144,72,0.22)',
                      borderRadius: '2px',
                      background: 'rgba(8,2,4,0.55)',
                    }} />
                  <button onClick={pasteScan} data-testid="soul-scanner-paste"
                    className="px-3 py-2 transition-all"
                    style={{
                      background: 'rgba(200,144,72,0.08)',
                      border: '1px solid rgba(200,144,72,0.25)',
                      borderRadius: '2px',
                      color: 'rgba(200,144,72,0.75)',
                      fontFamily: "'Cinzel', serif",
                      fontSize: '10px',
                      letterSpacing: '0.25em',
                      textTransform: 'uppercase',
                    }}>
                    paste
                  </button>
                  <button onClick={submitScan} disabled={loading || !scanWallet.trim()}
                    data-testid="soul-scanner-submit"
                    className="px-5 py-2 transition-all"
                    style={{
                      background: scanWallet.trim() && !loading
                        ? 'linear-gradient(180deg, rgba(200,144,72,0.25), rgba(140,80,30,0.18))'
                        : 'rgba(40,20,12,0.4)',
                      border: '1px solid rgba(200,144,72,0.45)',
                      borderRadius: '2px',
                      color: scanWallet.trim() && !loading ? '#ffd680' : 'rgba(200,144,72,0.35)',
                      cursor: scanWallet.trim() && !loading ? 'pointer' : 'not-allowed',
                      fontFamily: "'Cinzel', serif",
                      fontSize: '11px',
                      letterSpacing: '0.28em',
                      textTransform: 'uppercase',
                      fontWeight: 600,
                    }}>
                    {scanMode === 'token' ? 'investigate' : 'summon reading'}
                  </button>
                </div>
                {scanError && (
                  <p className="mt-2 text-[10px] font-mono tracking-[0.18em]" data-testid="soul-scanner-error"
                    style={{ color: 'rgba(220,80,60,0.7)' }}>
                    ⚠ {scanError}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* === CONVERSATION === */}
        <main ref={scrollRef} className="flex-1 overflow-y-auto chan-scroll px-4 sm:px-6 lg:px-10 pt-6 pb-40">
          {/* WHISPER NETWORK BANNER */}
          {whisperOpen && whispers && whispers.whispers && whispers.whispers.length > 0 && (
            <div className="max-w-3xl mx-auto mb-6" data-testid="whisper-banner"
              style={{ animation: 'chan-fade-in 0.7s ease-out' }}>
              <div className="relative px-5 py-4"
                style={{
                  background: 'linear-gradient(180deg, rgba(40,12,30,0.7) 0%, rgba(20,6,16,0.85) 100%)',
                  border: '1px solid rgba(180,140,220,0.32)',
                  borderRadius: '3px',
                  boxShadow: '0 0 28px rgba(140,80,180,0.18)',
                }}>
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-[14px]" style={{
                      color: '#c89048',
                      animation: 'chan-pulse 2s ease-in-out infinite',
                    }}>⌛</span>
                    <div>
                      <div style={{
                        fontFamily: "'Cinzel Decorative', 'Cinzel', serif",
                        fontSize: '13px',
                        color: 'rgba(220,180,255,0.95)',
                        letterSpacing: '0.22em',
                        fontWeight: 700,
                      }}>
                        WHILE YOU SLEPT…
                      </div>
                      <div className="text-[8px] font-mono tracking-[0.4em] uppercase mt-0.5"
                        style={{ color: 'rgba(220,180,255,0.55)' }}>
                        {whispers.whispers.length} of {whispers.watched_count} watched souls have stirred
                      </div>
                    </div>
                  </div>
                  <button onClick={() => setWhisperOpen(false)} data-testid="whisper-banner-close"
                    className="text-[10px] font-mono tracking-[0.2em] flex items-center gap-1 transition-opacity"
                    style={{ color: 'rgba(220,180,255,0.45)' }}>
                    <X className="w-3 h-3" /> dismiss
                  </button>
                </div>
                <div className="space-y-1.5">
                  {whispers.whispers.slice(0, 5).map((w) => (
                    <button key={w.wallet}
                      onClick={() => { setWhisperOpen(false); send('Tell me what stirred. Read this soul again. What has changed?', { scanWallet: w.wallet }); }}
                      data-testid={`whisper-${w.wallet.slice(0,6)}`}
                      className="group w-full flex items-center gap-3 px-3 py-2 transition-all hover:bg-[rgba(40,18,40,0.4)] text-left"
                      style={{
                        border: '1px solid rgba(180,140,220,0.14)',
                        borderRadius: '2px',
                      }}>
                      <span className="inline-block w-1.5 h-1.5 rounded-full shrink-0" style={{
                        background: '#c8a0ff',
                        boxShadow: '0 0 6px rgba(200,160,255,0.7)',
                        animation: 'chan-pulse 2s ease-in-out infinite',
                      }} />
                      <div className="flex-1 min-w-0">
                        <div className="text-[10px] font-mono tracking-[0.06em]"
                          style={{ color: 'rgba(230,220,200,0.85)' }}>
                          {w.wallet_short}
                        </div>
                        <div className="text-[8px] font-mono tracking-[0.22em] uppercase mt-0.5"
                          style={{ color: 'rgba(220,180,255,0.65)' }}>
                          {w.activity_count} new {w.activity_count === 1 ? 'movement' : 'movements'}
                          {w.first_seen_age && ` · last stir ${w.first_seen_age}`}
                        </div>
                      </div>
                      <span className="text-[8px] font-mono tracking-[0.32em] uppercase opacity-50 group-hover:opacity-100"
                        style={{ color: 'rgba(220,180,255,0.85)' }}>
                        read again ›
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {messages.length === 0 ? (
            /* === CLAUDE/CHATGPT-STYLE WELCOME (centered input, no chat above) === */
            <div
              className="max-w-2xl mx-auto flex flex-col items-center justify-center text-center"
              style={{ minHeight: 'calc(100vh - 240px)', animation: 'chan-fade-in 0.7s ease-out' }}
              data-testid="chancellor-welcome"
            >
              <div className="mb-7" style={{ animation: 'chan-fade-in 0.9s ease-out' }}>
                <div className="text-[9px] font-mono tracking-[0.5em] uppercase mb-3"
                  style={{ color: 'rgba(200,144,72,0.55)' }}>
                  · the chamber awaits ·
                </div>
                <h1
                  className="font-serif"
                  style={{
                    fontFamily: "'Cinzel Decorative','Cinzel',serif",
                    fontSize: 'clamp(2rem, 4.5vw, 3rem)',
                    color: '#e6dcc8',
                    letterSpacing: '0.04em',
                    lineHeight: 1.1,
                    textShadow: '0 0 24px rgba(200,65,60,0.28), 0 0 36px rgba(220,200,120,0.15)',
                  }}
                >
                  Speak, wanderer.
                </h1>
                <p className="italic mt-3"
                  style={{
                    fontFamily: '"EB Garamond", Georgia, serif',
                    fontSize: 'clamp(0.95rem, 1.2vw, 1.1rem)',
                    color: 'rgba(230,220,200,0.65)',
                    lineHeight: 1.5,
                  }}
                >
                  I have heard a thousand confessions. I remember each one.
                </p>
              </div>

              {/* Centered composer */}
              <div className="w-full max-w-xl">
                <div className="relative flex items-center gap-2 px-3 py-3"
                  style={{
                    background: 'linear-gradient(180deg, rgba(20,6,8,0.82) 0%, rgba(10,2,4,0.92) 100%)',
                    border: '1px solid rgba(200,144,72,0.5)',
                    borderRadius: '999px',
                    boxShadow: '0 0 0 1px rgba(0,0,0,0.5) inset, 0 0 36px rgba(200,144,72,0.18), 0 8px 32px rgba(0,0,0,0.7)',
                  }}>
                  <span className="pl-3" style={{ color: '#c89048', fontFamily: "'Cinzel', serif", fontSize: '15px', opacity: 0.85 }}>›</span>
                  <input ref={inputRef} type="text" value={input}
                    autoFocus
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={onKey}
                    disabled={loading}
                    maxLength={1500}
                    placeholder="speak to the chancellor..."
                    data-testid="chancellor-input"
                    className="flex-1 bg-transparent outline-none placeholder:opacity-40"
                    style={{
                      color: '#e6dcc8',
                      fontFamily: "'Cinzel', serif",
                      letterSpacing: '0.04em',
                      fontSize: '14px',
                    }} />
                  <button onClick={() => send()} disabled={loading || !input.trim()}
                    data-testid="chancellor-send"
                    className="group flex items-center justify-center w-11 h-11 transition-all"
                    style={{
                      background: input.trim() && !loading
                        ? 'linear-gradient(180deg, rgba(200,144,72,0.32), rgba(140,80,30,0.20))'
                        : 'rgba(40,20,12,0.4)',
                      border: '1px solid rgba(200,144,72,0.5)',
                      borderRadius: '999px',
                      color: input.trim() && !loading ? '#ffd680' : 'rgba(200,144,72,0.4)',
                      cursor: input.trim() && !loading ? 'pointer' : 'not-allowed',
                    }}>
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Suggested petitions chips */}
              <div className="w-full max-w-2xl mt-8"
                data-testid="chancellor-suggestions"
                style={{ animation: 'chan-fade-in 1.1s ease-out' }}>
                <div className="text-[9px] font-mono tracking-[0.45em] uppercase mb-3"
                  style={{ color: 'rgba(200,144,72,0.45)' }}>
                  · suggested petitions ·
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {SUGGESTED_OPENINGS.map((s, i) => {
                    const Icon = s.icon;
                    return (
                      <button key={i} onClick={() => send(s.text)} disabled={loading}
                        className="group text-left px-4 py-3 transition-all duration-300 hover:scale-[1.015] flex items-start gap-3"
                        style={{
                          background: 'linear-gradient(180deg, rgba(40,12,8,0.4) 0%, rgba(20,6,4,0.5) 100%)',
                          border: '1px solid rgba(200,144,72,0.22)',
                          color: 'rgba(230,220,200,0.78)',
                          fontFamily: "'Cinzel', serif",
                          fontSize: '12px',
                          letterSpacing: '0.04em',
                          borderRadius: '2px',
                        }}>
                        <Icon className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: 'rgba(200,144,72,0.65)' }} />
                        <span>{s.text}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="max-w-3xl mx-auto space-y-7" data-testid="chancellor-thread">
                {messages.map((m, idx) => (
                  <MessageBubble key={idx} role={m.role} text={m.text} error={m.error}
                    walletSeen={m.walletSeen} targetSeen={m.targetSeen} scanTarget={m.scanTarget}
                    soulCardId={m.soulCardId} memoryRecalled={m.memoryRecalled}
                    txSignature={m.txSignature} txIsMock={m.txIsMock} onchainError={m.onchainError}
                    onShare={(card) => setShareCard(card)}
                    animate={m.role === 'chancellor' && idx > 0 && idx === messages.length - 1} />
                ))}
                {loading && <TypingIndicator />}
              </div>
            </>
          )}
        </main>

        {/* === STICKY COMPOSER (hidden in welcome state) === */}
        {messages.length > 0 && (
        <div className="fixed bottom-0 z-20 px-4 sm:px-6 lg:px-10 pb-5 pt-8 pointer-events-none"
          style={{
            background: 'linear-gradient(180deg, transparent 0%, rgba(5,2,8,0.92) 35%, rgba(5,2,8,0.98) 100%)',
            left: 0,
            right: 0,
          }}>
          <div className={`max-w-3xl mx-auto pointer-events-auto ${desktopSidebarOpen ? 'lg:ml-[calc(280px+max(40px,(100vw-280px-768px)/2))] lg:mr-[max(40px,(100vw-280px-768px)/2)]' : 'lg:ml-[max(40px,(100vw-768px)/2)] lg:mr-[max(40px,(100vw-768px)/2)]'}`}>
            <div className="relative flex items-center gap-2 px-3 py-2.5"
              style={{
                background: 'linear-gradient(180deg, rgba(20,6,8,0.82) 0%, rgba(10,2,4,0.92) 100%)',
                border: '1px solid rgba(200,144,72,0.32)',
                borderRadius: '999px',
                boxShadow: '0 0 0 1px rgba(0,0,0,0.5) inset, 0 0 28px rgba(140,40,20,0.18), 0 8px 24px rgba(0,0,0,0.7)',
              }}>
              <span className="pl-3" style={{ color: '#c89048', fontFamily: "'Cinzel', serif", fontSize: '14px', opacity: 0.7 }}>›</span>
              <input ref={inputRef} type="text" value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKey}
                disabled={loading}
                maxLength={1500}
                placeholder="speak to the chancellor..."
                data-testid="chancellor-input"
                className="flex-1 bg-transparent outline-none font-serif text-sm placeholder:opacity-40"
                style={{
                  color: '#e6dcc8',
                  fontFamily: "'Cinzel', serif",
                  letterSpacing: '0.04em',
                }} />
              <button onClick={() => send()} disabled={loading || !input.trim()}
                data-testid="chancellor-send"
                className="group flex items-center justify-center w-10 h-10 transition-all"
                style={{
                  background: input.trim() && !loading
                    ? 'linear-gradient(180deg, rgba(200,144,72,0.28), rgba(140,80,30,0.18))'
                    : 'rgba(40,20,12,0.4)',
                  border: '1px solid rgba(200,144,72,0.4)',
                  borderRadius: '999px',
                  color: input.trim() && !loading ? '#ffd680' : 'rgba(200,144,72,0.4)',
                  cursor: input.trim() && !loading ? 'pointer' : 'not-allowed',
                }}>
                {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              </button>
            </div>
            <div className="mt-2 text-center text-[8px] font-mono tracking-[0.4em] uppercase"
              style={{ color: 'rgba(200,144,72,0.25)' }}>
              the chancellor watches all
            </div>
          </div>
        </div>
        )}
      </div>

      {/* === SOUL CARD SHARE MODAL === */}
      {shareCard && <ShareModal card={shareCard} apiBase={API_URL} onClose={() => setShareCard(null)} />}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
//  TYPEWRITER HOOK — progressive char reveal for chancellor replies
// ──────────────────────────────────────────────────────────────────────────────
function useTypewriter(text, enabled) {
  const [shown, setShown] = useState(enabled ? text.slice(0, 2) : text);
  const [done, setDone] = useState(!enabled);
  useEffect(() => {
    if (!enabled) { setShown(text); setDone(true); return; }
    let i = 2;
    setShown(text.slice(0, i));
    setDone(false);
    let raf = 0;
    let last = performance.now();
    const tick = (now) => {
      // ~3 chars per frame at 60fps ≈ 180 chars/sec
      const dt = now - last;
      last = now;
      const chars = Math.max(2, Math.round(dt / 16) * 3);
      i = Math.min(text.length, i + chars);
      setShown(text.slice(0, i));
      if (i < text.length) {
        raf = requestAnimationFrame(tick);
      } else {
        setDone(true);
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [text, enabled]);
  return { shown, done };
}

// ──────────────────────────────────────────────────────────────────────────────
//  SIDEBAR
// ──────────────────────────────────────────────────────────────────────────────
function Sidebar({ open, desktopOpen, onClose, wallet, recentReadings, onNewAudience, onPickReading }) {
  return (
    <>
      {/* Mobile backdrop */}
      {open && <div className="lg:hidden fixed inset-0 z-30" data-testid="chan-sidebar-backdrop"
        onClick={onClose}
        style={{ background: 'rgba(5,2,8,0.85)', backdropFilter: 'blur(6px)' }} />}

      <aside data-testid="chan-sidebar"
        className={`fixed top-0 left-0 z-40 h-screen w-[280px] flex flex-col transition-transform duration-300 ${open ? 'translate-x-0' : '-translate-x-full'} ${desktopOpen ? 'lg:translate-x-0' : 'lg:-translate-x-full'}`}
        style={{
          background: 'linear-gradient(180deg, rgba(15,4,8,0.95) 0%, rgba(8,2,4,0.98) 100%)',
          borderRight: '1px solid rgba(200,144,72,0.18)',
        }}>

        {/* IDENTITY HEADER */}
        <div className="px-5 pt-6 pb-5" style={{ borderBottom: '1px solid rgba(200,144,72,0.14)' }}>
          <div className="flex items-start gap-3">
            <div className="relative shrink-0" style={{ width: '52px', height: '52px' }}>
              {/* Halo */}
              <div className="absolute pointer-events-none rounded-full"
                style={{
                  width: '90px', height: '90px',
                  left: '50%', top: '50%',
                  transform: 'translate(-50%,-50%)',
                  background: 'radial-gradient(circle, rgba(200,144,72,0.22) 0%, rgba(120,40,20,0.10) 40%, transparent 75%)',
                }} />
              <img src={chancellorPortraitHead} alt="" draggable="false"
                className="relative w-full h-full select-none pointer-events-none"
                style={{
                  animation: 'chan-mask-breathe 5s ease-in-out infinite',
                  objectFit: 'cover',
                  objectPosition: '50% 25%',
                  borderRadius: '50%',
                  border: '1px solid rgba(200,144,72,0.32)',
                }} />
            </div>
            <div className="flex-1 min-w-0">
              <div style={{
                fontFamily: "'Cinzel Decorative', 'Cinzel', serif",
                fontSize: '14px',
                color: '#c89048',
                letterSpacing: '0.22em',
                textShadow: '0 0 12px rgba(200,144,72,0.35)',
                fontWeight: 700,
              }}>
                THE CHANCELLOR
              </div>
              <div className="mt-1 flex items-center gap-1.5">
                <span className="inline-block w-1.5 h-1.5 rounded-full" style={{
                  background: '#88dd88',
                  boxShadow: '0 0 6px rgba(136,221,136,0.7)',
                  animation: 'chan-pulse 2s ease-in-out infinite',
                }} />
                <span className="text-[8px] font-mono tracking-[0.32em] uppercase"
                  style={{ color: 'rgba(180,220,200,0.75)' }}>
                  presiding · awake
                </span>
              </div>
            </div>
            <button onClick={onClose} className="lg:hidden p-1" data-testid="chan-sidebar-close"
              style={{ color: 'rgba(200,144,72,0.5)' }}>
              <X className="w-4 h-4" />
            </button>
          </div>

          {wallet ? (
            <p className="mt-3.5 text-[9px] font-mono tracking-[0.18em]"
              style={{ color: 'rgba(200,144,72,0.55)' }} data-testid="chancellor-wallet-bound">
              soul bound · {wallet.startsWith('Demo') ? 'demo wanderer' : `${wallet.slice(0,4)}…${wallet.slice(-4)}`}
            </p>
          ) : (
            <p className="mt-3.5 text-[9px] font-mono tracking-[0.18em]"
              style={{ color: 'rgba(140,80,60,0.55)' }} data-testid="chancellor-wallet-unbound">
              no soul bound · connect for deeper sight
            </p>
          )}
        </div>

        {/* NEW AUDIENCE BUTTON */}
        <div className="px-3 pt-3 pb-2">
          <button onClick={onNewAudience} data-testid="chan-new-audience"
            className="w-full flex items-center gap-2 px-3 py-2.5 transition-all hover:scale-[1.01]"
            style={{
              background: 'linear-gradient(180deg, rgba(200,144,72,0.14), rgba(140,80,30,0.08))',
              border: '1px solid rgba(200,144,72,0.32)',
              borderRadius: '2px',
              color: '#ffd680',
              fontFamily: "'Cinzel', serif",
              fontSize: '11px',
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              fontWeight: 600,
            }}>
            <Plus className="w-3.5 h-3.5" />
            <span>new audience</span>
          </button>
          <Link to="/court" data-testid="chan-court-link"
            className="mt-2 w-full flex items-center gap-2 px-3 py-2.5 transition-all hover:scale-[1.01]"
            style={{
              background: 'linear-gradient(180deg, rgba(40,12,8,0.5), rgba(20,6,4,0.6))',
              border: '1px solid rgba(220,80,60,0.32)',
              borderRadius: '2px',
              color: 'rgba(255,180,160,0.92)',
              fontFamily: "'Cinzel', serif",
              fontSize: '11px',
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              fontWeight: 600,
              textDecoration: 'none',
            }}>
            <Crown className="w-3.5 h-3.5" />
            <span>visit the court</span>
            <ChevronRight className="w-3 h-3 ml-auto opacity-60" />
          </Link>
        </div>

        {/* CAPABILITIES */}
        <div className="px-5 pt-4 pb-2">
          <div className="text-[8px] font-mono tracking-[0.4em] uppercase mb-2.5"
            style={{ color: 'rgba(200,144,72,0.5)' }}>
            · capabilities ·
          </div>
          <div className="space-y-1.5">
            <Capability icon={Coins} label="Live token prices" hint="CoinGecko + DEX" />
            <Capability icon={Sparkles} label="Wallet scanner" hint="Helius DAS · 100% public" />
            <Capability icon={Activity} label="Solana network pulse" hint="real-time slot + TPS" />
            <Capability icon={Scroll} label="$GFG lore oracle" hint="seasons, descent, passport" />
          </div>
        </div>

        {/* RECENTLY JUDGED */}
        <div className="px-5 pt-4 pb-2 flex-1 min-h-0 flex flex-col">
          <div className="text-[8px] font-mono tracking-[0.4em] uppercase mb-2.5 flex items-center justify-between"
            style={{ color: 'rgba(200,144,72,0.5)' }}>
            <span>· recently judged ·</span>
            <span style={{ color: 'rgba(200,144,72,0.32)' }}>{recentReadings.length || 0}</span>
          </div>
          <div className="flex-1 overflow-y-auto chan-scroll space-y-1 pr-1">
            {recentReadings.length === 0 ? (
              <p className="text-[10px] font-mono tracking-[0.15em]"
                style={{ color: 'rgba(230,220,200,0.32)' }}>
                no souls judged yet. be the first.
              </p>
            ) : recentReadings.map((r) => (
              <button key={r.card_id} onClick={() => onPickReading(r)}
                data-testid={`recent-reading-${r.card_id}`}
                className="group w-full flex items-center gap-2.5 px-2.5 py-2 transition-all text-left hover:bg-[rgba(40,18,10,0.4)]"
                style={{
                  border: '1px solid rgba(200,144,72,0.10)',
                  borderRadius: '2px',
                }}>
                <span className="inline-block w-1.5 h-1.5 rounded-full shrink-0"
                  style={{
                    background: TIER_COLORS[r.tier] || '#a0a0a0',
                    boxShadow: `0 0 6px ${TIER_COLORS[r.tier] || '#a0a0a0'}66`,
                  }} />
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] font-mono tracking-[0.12em] truncate"
                    style={{ color: 'rgba(230,220,200,0.85)' }}>
                    {r.wallet_short}
                  </div>
                  <div className="text-[8px] font-mono tracking-[0.22em] uppercase mt-0.5"
                    style={{ color: TIER_COLORS[r.tier] || 'rgba(200,144,72,0.5)' }}>
                    {r.tier || 'unknown'} · {(r.sol_balance || 0).toFixed(2)} SOL
                  </div>
                </div>
                <ChevronRight className="w-3 h-3 opacity-30 group-hover:opacity-70 shrink-0"
                  style={{ color: '#c89048' }} />
              </button>
            ))}
          </div>
        </div>

        {/* FOOTER */}
        <div className="px-5 py-3" style={{ borderTop: '1px solid rgba(200,144,72,0.14)' }}>
          <p className="text-[8px] font-mono tracking-[0.32em] uppercase text-center"
            style={{ color: 'rgba(200,144,72,0.32)' }}>
            ◆ gas-free gremlins ◆
          </p>
        </div>
      </aside>
    </>
  );
}

function Capability({ icon: Icon, label, hint, onClick }) {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag onClick={onClick}
      className={`w-full flex items-start gap-2.5 px-2.5 py-2 ${onClick ? 'cursor-pointer hover:bg-[rgba(40,18,10,0.4)] transition-colors' : ''}`}
      style={{
        border: '1px solid rgba(200,144,72,0.10)',
        borderRadius: '2px',
        textAlign: 'left',
      }}>
      <Icon className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: 'rgba(200,144,72,0.7)' }} />
      <div className="flex-1 min-w-0">
        <div className="text-[10.5px] font-serif tracking-[0.04em]"
          style={{ color: 'rgba(230,220,200,0.85)', fontFamily: "'Cinzel', serif" }}>
          {label}
        </div>
        <div className="text-[8.5px] font-mono tracking-[0.18em] mt-0.5 uppercase"
          style={{ color: 'rgba(200,144,72,0.45)' }}>
          {hint}
        </div>
      </div>
      {onClick && <ChevronRight className="w-3 h-3 opacity-50 shrink-0" style={{ color: '#c89048' }} />}
    </Tag>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
//  MESSAGE BUBBLE
// ──────────────────────────────────────────────────────────────────────────────
function MessageBubble({ role, text, error, walletSeen, targetSeen, scanTarget, soulCardId, onShare, animate, memoryRecalled, txSignature, txIsMock, onchainError }) {
  const isChancellor = role === 'chancellor';
  const shortAddr = scanTarget ? `${scanTarget.slice(0,4)}…${scanTarget.slice(-4)}` : '';
  const shouldType = isChancellor && !error && !!animate;
  const { shown, done } = useTypewriter(text || '', shouldType);
  const displayText = isChancellor ? shown : text;
  const isTyping = isChancellor && shouldType && !done;
  return (
    <div className="flex" style={{
      justifyContent: isChancellor ? 'flex-start' : 'flex-end',
      animation: 'chan-msg-in 0.6s cubic-bezier(.2,.6,.4,1) both',
    }} data-testid={isChancellor ? 'msg-chancellor' : 'msg-soul'}>
      <div className="max-w-[88%] sm:max-w-[78%]">
        {isChancellor && (
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            <span className="text-[8px] font-mono tracking-[0.4em] uppercase"
              style={{ color: error ? 'rgba(220,40,60,0.5)' : 'rgba(200,144,72,0.6)' }}>
              the chancellor
            </span>
            {walletSeen && (
              <span className="text-[7px] font-mono tracking-[0.3em] uppercase px-1.5 py-px"
                style={{ color: 'rgba(180,220,200,0.65)', border: '1px solid rgba(120,200,160,0.25)', borderRadius: '1px' }}>
                soul read
              </span>
            )}
            {targetSeen && shortAddr && (
              <span className="text-[7px] font-mono tracking-[0.3em] uppercase px-1.5 py-px"
                data-testid="msg-target-badge"
                style={{ color: 'rgba(255,170,80,0.85)', border: '1px solid rgba(200,144,72,0.45)', background: 'rgba(140,80,30,0.12)', borderRadius: '1px' }}>
                ⚖ judged · {shortAddr}
              </span>
            )}
            {memoryRecalled && (
              <span className="text-[7px] font-mono tracking-[0.3em] uppercase px-1.5 py-px"
                data-testid="msg-memory-badge"
                title="The Chancellor remembers this soul from a prior reading"
                style={{ color: 'rgba(220,180,255,0.85)', border: '1px solid rgba(180,140,220,0.45)', background: 'rgba(80,40,140,0.12)', borderRadius: '1px' }}>
                ⌛ remembered
              </span>
            )}
          </div>
        )}
        {!isChancellor && scanTarget && (
          <div className="mb-1.5 flex justify-end">
            <span className="text-[7px] font-mono tracking-[0.3em] uppercase px-1.5 py-px"
              data-testid="msg-soul-scan-chip"
              style={{ color: 'rgba(200,144,72,0.7)', border: '1px solid rgba(200,144,72,0.32)', background: 'rgba(40,20,12,0.4)', borderRadius: '1px' }}>
              ⚖ scanning · {shortAddr}
            </span>
          </div>
        )}
        <div style={{
          padding: isChancellor ? '14px 18px' : '12px 16px',
          background: isChancellor
            ? 'linear-gradient(180deg, rgba(30,10,8,0.55) 0%, rgba(15,5,4,0.65) 100%)'
            : 'linear-gradient(180deg, rgba(40,18,10,0.4) 0%, rgba(25,10,6,0.5) 100%)',
          borderLeft: isChancellor ? `2px solid ${error ? 'rgba(220,40,60,0.5)' : 'rgba(200,144,72,0.55)'}` : 'none',
          borderRight: isChancellor ? 'none' : '2px solid rgba(140,80,30,0.45)',
          borderRadius: '2px',
          color: isChancellor ? '#e6dcc8' : 'rgba(220,200,170,0.92)',
          fontFamily: isChancellor ? "'Cinzel', 'Cinzel Decorative', serif" : "'Cinzel', serif",
          fontSize: isChancellor ? '14.5px' : '14px',
          lineHeight: 1.65,
          letterSpacing: '0.015em',
          whiteSpace: 'pre-wrap',
          textShadow: isChancellor ? '0 0 10px rgba(120,40,20,0.18)' : 'none',
        }}>
          {displayText}
          {isTyping && (
            <span aria-hidden className="inline-block align-middle ml-0.5"
              style={{
                width: '8px', height: '14px',
                background: 'linear-gradient(180deg, #ffaa30 0%, #c8722a 100%)',
                boxShadow: '0 0 8px rgba(255,140,40,0.7), 0 0 16px rgba(220,80,30,0.5)',
                animation: 'chan-typing 0.85s ease-in-out infinite',
                marginBottom: '2px',
              }} />
          )}
        </div>
        {isChancellor && soulCardId && !error && done && (
          <div className="mt-2.5 flex justify-start" style={{ animation: 'chan-fade-in 0.5s ease-out' }}>
            <button onClick={() => onShare && onShare({ id: soulCardId, scanTarget })}
              data-testid={`forge-soul-card-${soulCardId}`}
              className="group inline-flex items-center gap-2 px-3 py-1.5 transition-all hover:scale-[1.02]"
              style={{
                background: 'linear-gradient(180deg, rgba(40,18,10,0.45) 0%, rgba(20,8,4,0.55) 100%)',
                border: '1px solid rgba(200,144,72,0.32)',
                borderRadius: '2px',
                color: 'rgba(255,170,80,0.85)',
                fontFamily: "'Cinzel', serif",
                fontSize: '10px',
                letterSpacing: '0.32em',
                textTransform: 'uppercase',
                fontWeight: 600,
                boxShadow: '0 0 18px rgba(140,40,20,0.12)',
              }}>
              <span style={{ opacity: 0.75 }}>⚖</span>
              forge soul card
              <span style={{ opacity: 0.6 }}>›</span>
            </button>
          </div>
        )}
        {/* On-chain proof chip — real Solana Explorer link when live mode wrote the verdict */}
        {isChancellor && txSignature && !txIsMock && !error && done && (
          <div className="mt-2 flex justify-start" style={{ animation: 'chan-fade-in 0.5s ease-out' }}>
            <a
              href={explorerTx(txSignature)}
              target="_blank" rel="noopener noreferrer"
              data-testid="msg-onchain-chip"
              className="inline-flex items-center gap-1.5 px-2.5 py-1 transition-all hover:scale-[1.02] hover:brightness-125"
              style={{
                background: 'linear-gradient(180deg, rgba(8,18,28,0.85) 0%, rgba(4,12,18,0.95) 100%)',
                border: '1px solid rgba(125,208,255,0.5)',
                borderRadius: '2px',
                color: '#a8def7',
                fontFamily: "'JetBrains Mono','Courier New',monospace",
                fontSize: '9.5px',
                letterSpacing: '0.22em',
                textTransform: 'uppercase',
                fontWeight: 700,
                boxShadow: '0 0 12px rgba(125,208,255,0.18)',
              }}
            >
              <span className="inline-block rounded-full"
                style={{ width: 5, height: 5, background: '#7dd0ff', boxShadow: '0 0 6px #7dd0ff' }} />
              ON-CHAIN · view tx
              <span style={{ opacity: 0.7 }}>↗</span>
            </a>
          </div>
        )}
        {/* On-chain attempt failed in live mode — surface it inline so the user knows */}
        {isChancellor && onchainError && txIsMock && !error && done && (
          <div className="mt-2 flex justify-start">
            <span
              data-testid="msg-onchain-error"
              title={onchainError}
              className="inline-flex items-center gap-1.5 px-2 py-0.5"
              style={{
                background: 'rgba(60,30,30,0.7)',
                border: '1px solid rgba(255,138,108,0.45)',
                borderRadius: '2px',
                color: '#ff8b6b',
                fontFamily: "'JetBrains Mono','Courier New',monospace",
                fontSize: '9px',
                letterSpacing: '0.22em',
                textTransform: 'uppercase',
                fontWeight: 700,
              }}
            >
              ⚠ on-chain write skipped
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function TypingIndicator() {
  return (
    <div className="flex items-center gap-2.5" data-testid="chancellor-typing">
      <span className="text-[8px] font-mono tracking-[0.4em] uppercase"
        style={{ color: 'rgba(200,144,72,0.5)' }}>
        the chancellor weighs
      </span>
      <div className="flex gap-1.5">
        {[0, 1, 2].map(i => (
          <span key={i} className="w-1 h-1 rounded-full"
            style={{
              background: '#c89048',
              boxShadow: '0 0 4px rgba(200,144,72,0.7)',
              animation: `chan-typing 1.2s ease-in-out ${i * 0.18}s infinite`,
            }} />
        ))}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
//  SHARE MODAL
// ──────────────────────────────────────────────────────────────────────────────
function ShareModal({ card, apiBase, onClose }) {
  const [copied, setCopied] = useState(false);
  const shareUrl = `${apiBase}/api/chancellor/share/${card.id}`;
  const imageUrl = `${apiBase}/api/chancellor/soul-card/${card.id}.png`;
  const tweetText = card.scanTarget
    ? `The Chancellor has rendered judgement upon ${card.scanTarget.slice(0,4)}…${card.scanTarget.slice(-4)}.\n\nRead your own soul:`
    : "The Chancellor has spoken. Read your soul:";
  const tweetUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}&url=${encodeURIComponent(shareUrl)}`;

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch (_) { /* ignore */ }
  };

  return (
    <div data-testid="soul-card-modal"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(5,2,8,0.92)', backdropFilter: 'blur(8px)', animation: 'chan-fade-in 0.3s ease-out' }}
      onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-2xl"
        style={{
          background: 'linear-gradient(180deg, rgba(30,8,6,0.95) 0%, rgba(15,4,3,0.98) 100%)',
          border: '1px solid rgba(200,144,72,0.45)',
          borderRadius: '3px',
          boxShadow: '0 0 50px rgba(220,38,38,0.18), 0 12px 40px rgba(0,0,0,0.8)',
        }}>
        <button onClick={onClose} data-testid="soul-card-close"
          className="absolute top-3 right-3 z-10 transition-opacity hover:opacity-100"
          style={{
            color: 'rgba(200,144,72,0.5)', opacity: 0.7,
            fontSize: '13px', fontFamily: 'monospace', letterSpacing: '0.15em',
            background: 'transparent', border: 'none', cursor: 'pointer',
            padding: '4px 10px',
          }}>
          × close
        </button>

        <div className="px-6 sm:px-8 pt-7 pb-6">
          <div className="text-center mb-5">
            <div className="text-[9px] font-mono tracking-[0.45em] uppercase mb-1"
              style={{ color: 'rgba(200,144,72,0.55)' }}>
              ◆ ◆ ◆
            </div>
            <h2 style={{
              fontFamily: "'Cinzel Decorative', 'Cinzel', serif",
              fontSize: '20px',
              color: '#c89048',
              letterSpacing: '0.28em',
              textShadow: '0 0 20px rgba(200,144,72,0.35), 0 0 40px rgba(140,40,20,0.2)',
            }}>
              SOUL CARD FORGED
            </h2>
            <p className="mt-1 text-[10px] font-mono tracking-[0.32em] uppercase"
              style={{ color: 'rgba(220,80,60,0.65)' }}>
              · share this verdict ·
            </p>
          </div>

          <div className="relative" data-testid="soul-card-preview"
            style={{
              border: '1px solid rgba(200,144,72,0.35)',
              padding: '4px',
              background: 'rgba(8,2,4,0.6)',
              boxShadow: 'inset 0 0 18px rgba(220,38,38,0.12)',
            }}>
            <img src={imageUrl} alt="The Chancellor's Verdict"
              draggable="false"
              className="block w-full h-auto"
              style={{ aspectRatio: '1200/630', background: '#0a0405' }} />
          </div>

          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            <a href={tweetUrl} target="_blank" rel="noreferrer"
              data-testid="soul-card-tweet"
              className="group inline-flex items-center justify-center gap-2 px-5 py-3 transition-all hover:scale-[1.01]"
              style={{
                background: 'linear-gradient(180deg, rgba(200,144,72,0.25), rgba(140,80,30,0.18))',
                border: '1px solid rgba(200,144,72,0.55)',
                color: '#ffd680',
                fontFamily: "'Cinzel', serif",
                fontSize: '11px',
                letterSpacing: '0.32em',
                textTransform: 'uppercase',
                fontWeight: 700,
                textDecoration: 'none',
                borderRadius: '2px',
              }}>
              <span style={{ opacity: 0.85 }}>𝕏</span>
              <span>tweet the verdict</span>
            </a>
            <button onClick={copyLink} data-testid="soul-card-copy"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 transition-all hover:scale-[1.01]"
              style={{
                background: copied
                  ? 'linear-gradient(180deg, rgba(120,200,160,0.18), rgba(60,140,100,0.12))'
                  : 'linear-gradient(180deg, rgba(40,18,10,0.55), rgba(20,8,4,0.65))',
                border: copied
                  ? '1px solid rgba(120,200,160,0.55)'
                  : '1px solid rgba(200,144,72,0.35)',
                color: copied ? 'rgba(180,220,200,0.95)' : 'rgba(200,144,72,0.85)',
                fontFamily: "'Cinzel', serif",
                fontSize: '11px',
                letterSpacing: '0.32em',
                textTransform: 'uppercase',
                fontWeight: 700,
                cursor: 'pointer',
                borderRadius: '2px',
              }}>
              {copied ? '✓ link copied' : '⎘ copy link'}
            </button>
          </div>

          <p className="mt-4 text-center text-[9px] font-mono tracking-[0.32em] uppercase"
            style={{ color: 'rgba(230,220,200,0.4)' }}>
            previews live on x · discord · imessage
          </p>
        </div>
      </div>
    </div>
  );
}
